import React, { createContext, ReactNode, useMemo, useState } from 'react';

type CreditCardFormContextType = {
  nameOnCard: string;
  nameOnCardError: string | undefined;
  generalStripeError: string | undefined;
  cardNumberError: string | undefined;
  cardExpiryError: string | undefined;
  cardCvcError: string | undefined;
  cardPostalCodeError: string | undefined;
};

type CreditCardFormDispatchContextType = {
  setNameOnCard: React.Dispatch<React.SetStateAction<string>>;
  setNameOnCardError: React.Dispatch<React.SetStateAction<string | undefined>>;
  setGeneralStripeError: React.Dispatch<React.SetStateAction<string | undefined>>;
  setCardNumberError: React.Dispatch<React.SetStateAction<string | undefined>>;
  setCardExpiryError: React.Dispatch<React.SetStateAction<string | undefined>>;
  setCardCvcError: React.Dispatch<React.SetStateAction<string | undefined>>;
  setCardPostalCodeError: React.Dispatch<React.SetStateAction<string | undefined>>;
};

export const CreditCardFormContext = createContext<CreditCardFormContextType>({
  nameOnCard: '',
  nameOnCardError: undefined,
  generalStripeError: undefined,
  cardNumberError: undefined,
  cardExpiryError: undefined,
  cardCvcError: undefined,
  cardPostalCodeError: undefined,
});

export const CreditCardFormDispatchContext = createContext<CreditCardFormDispatchContextType>({
  setNameOnCard: () => null,
  setNameOnCardError: () => null,
  setGeneralStripeError: () => null,
  setCardNumberError: () => null,
  setCardExpiryError: () => null,
  setCardCvcError: () => null,
  setCardPostalCodeError: () => null,
});

const CreditCardFormProvider = ({ children }: { children: ReactNode }) => {
  const [nameOnCard, setNameOnCard] = useState('');
  const [nameOnCardError, setNameOnCardError] = useState<string | undefined>(undefined);
  const [generalStripeError, setGeneralStripeError] = useState<string | undefined>(undefined);
  const [cardNumberError, setCardNumberError] = useState<string | undefined>(undefined);
  const [cardExpiryError, setCardExpiryError] = useState<string | undefined>(undefined);
  const [cardCvcError, setCardCvcError] = useState<string | undefined>(undefined);
  const [cardPostalCodeError, setCardPostalCodeError] = useState<string | undefined>(undefined);

  const formValue = useMemo(
    () => ({
      nameOnCard,
      nameOnCardError,
      generalStripeError,
      cardNumberError,
      cardExpiryError,
      cardCvcError,
      cardPostalCodeError,
    }),
    [
      nameOnCard,
      nameOnCardError,
      generalStripeError,
      cardNumberError,
      cardExpiryError,
      cardCvcError,
      cardPostalCodeError,
    ]
  );

  const formDispatch = useMemo(
    () => ({
      setNameOnCard,
      setNameOnCardError,
      setGeneralStripeError,
      setCardNumberError,
      setCardExpiryError,
      setCardCvcError,
      setCardPostalCodeError,
    }),
    [
      setNameOnCard,
      setNameOnCardError,
      setGeneralStripeError,
      setCardNumberError,
      setCardExpiryError,
      setCardCvcError,
      setCardPostalCodeError,
    ]
  );

  return (
    <CreditCardFormContext.Provider value={formValue}>
      <CreditCardFormDispatchContext.Provider value={formDispatch}>{children}</CreditCardFormDispatchContext.Provider>
    </CreditCardFormContext.Provider>
  );
};

export default CreditCardFormProvider;
